// @flow
import React from "react";
import withRouter from "@tvg/utils/withCustomRouter";
import type { RouterHistory } from "react-router";
import { TopRacesComponent } from "@tvg/top-races";
import { get, noop, flowRight as compose, isEqual } from "lodash";
import TopRaces from "@fdr/atomic-ui/_templates/TopRaces";
import { TopRacesMask } from "@fdr/static-ui/Masks";
import type { Device } from "@tvg/types/Device";
import type { WagerProfile, UserOptedInPromos } from "@tvg/types/User";
import type { UnaryFn } from "@tvg/types/Functional";
import { graphql } from "@apollo/client/react/hoc";
import { connect } from "react-redux";
import { type Dispatch } from "redux";
import { type Actions } from "@tvg/top-races/src/actions";
import type { RacePanelLink, RacesActiveBets } from "@fdr/types/Race";
import mediator from "@tvg/mediator";
import { doesSilkLoads } from "@fdr/utils/raceUtils";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { getAccountNumber } from "@urp/store-selectors";

import ApolloOptions from "./graphql/options.graph";
import TopRacesQuery from "./graphql/queries/TopRaces.graphql";
import TopRacesRDAQuery from "./graphql/queries/TopRacesRDA.graphql";

type Props = {
  dispatch: Dispatch<Actions>,
  races: RacePanelLink[],
  isLoading: boolean,
  device: Device,
  optedInPromos: UserOptedInPromos,
  wagerProfile: WagerProfile,
  isLogged: boolean,
  shouldUpdate: boolean,
  rdaClient: mixed,
  title?: string,
  accountId: string,
  useRDAService: boolean,
  refetch: UnaryFn<mixed, void>,
  isEndOfFetch: boolean,
  isPoller: boolean,
  isOpeningApp: boolean,
  xsellURL: string,
  racesActiveBets: RacesActiveBets,
  history: RouterHistory,
  useIsPromoTagShownFlag: boolean,
  showHeader?: boolean,
  trackTagText: string
};

// $FlowFixMe
export class FDRTopRaces extends TopRacesComponent<Props> {
  static defaultProps = {
    races: [],
    isLoading: true,
    device: "mobile",
    wagerProfile: "PORT-Generic",
    optedInPromos: {},
    isLogged: false,
    shouldUpdate: false,
    rdaClient: noop,
    accountId: "",
    useRDAService: false,
    title: "Top Races",
    refetch: noop,
    isEndOfFetch: false,
    isPoller: true,
    isOpeningApp: false,
    xsellURL: "",
    racesActiveBets: {},
    useIsPromoTagShownFlag: false,
    showHeader: true,
    trackTagText: ""
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      shouldShowSilks: {}
    };
  }

  componentDidUpdate() {
    const { races } = this.props;

    if (!this.props.isLoading && races.length) {
      const shouldShowSilks = races.reduce(async (accumulator, race) => {
        const bettingInterests = get(race, "bettingInterests", []);
        const waitedAccumulator = await accumulator;
        if (bettingInterests.length) {
          const result = await doesSilkLoads(bettingInterests);

          waitedAccumulator[race.tvgRaceId] = result && this.props.showSilks;
        } else {
          waitedAccumulator[race.tvgRaceId] = false;
        }

        return waitedAccumulator;
      }, {});

      shouldShowSilks.then((result) => {
        this.setState({
          shouldShowSilks: result
        });
      });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: *) {
    const shouldShowSilksChanged =
      JSON.stringify(this.state.shouldShowSilks) !==
      JSON.stringify(nextState.shouldShowSilks);

    const didPropsChange = !isEqual(nextProps, this.props);

    return (
      super.shouldComponentUpdate(nextProps, nextState) ||
      shouldShowSilksChanged ||
      didPropsChange
    );
  }

  getTagDescription = (race: RacePanelLink) => {
    if (
      get(race, "promos[0].isAboveTheLine", false) ||
      get(race, "userPromotions.length", 0)
    ) {
      return get(race, "userPromotions[0].optedIn", false)
        ? "Promo Optin"
        : "Promo";
    }

    return "No promo";
  };

  redirectToApp = () => {
    if (typeof window !== "undefined") {
      mediator.base.dispatch({
        type: "XSELL_NAVIGATION"
      });
      window.location.assign(this.props.xsellURL);
    }
  };

  onSeeAllRacesHandler = () => {
    this.props.history.push("/tracks");
  };

  render = () =>
    (this.state.hasFetched || !!this.props.races.length) &&
    this.props.shouldUpdate ? (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <TopRaces
        isOpeningApp={this.props.isOpeningApp}
        onRunnerSelection={
          // $FlowFixMe
          this.onRunnerSelection
        }
        redirectToApp={this.redirectToApp}
        optedInPromos={this.props.optedInPromos}
        onHandleTransition={this.onHandleTransition}
        races={this.props.races}
        onCardClick={
          // $FlowFixMe
          this.onCardClick
        }
        activeIndex={this.state.activeIndex}
        racesToRender={2}
        // $FlowFixMe
        onLastCardClick={this.onLastCardClick}
        title={this.props.title}
        isLoading={this.props.isLoading}
        showLastCard={this.state.showLastCard}
        racesActiveBets={this.props.isLogged ? this.props.racesActiveBets : {}}
        showSilks={this.state.shouldShowSilks}
        onSeeAllRaces={this.onSeeAllRacesHandler}
        hasHeader={this.props.showHeader}
        trackTagText={this.props.trackTagText}
      />
    ) : (
      <TopRacesMask />
    );
}

const mapStateToProps = (store) => ({
  wagerProfile: get(store, "userData.user.profile", "FDR-Generic"),
  isLogged: get(store, "userData.logged"),
  isAccountCompliant: isAccountCompliantSelector(store),
  accountId: getAccountNumber(store),
  useRDAService: get(store, "capi.featureToggles.useRDAService", false),
  xsellURL: get(store, "capi.messages.xsellRedirectURL", ""),
  showSilks: get(store, "capi.featureToggles.showSilks", false),
  racesActiveBets: get(store, "myBets.racesActiveBets", {}),
  useIsPromoTagShownFlag: get(
    store,
    "capi.featureToggles.useIsPromoTagShownFlag",
    false
  ),
  trackTagText: get(store, "capi.messages.trackTagText", "")
});

export const skipTopRacesQuery = ({
  isLogged,
  isAccountCompliant,
  shouldUpdate,
  useRDAService,
  accountId
}: {
  isLogged: boolean,
  isAccountCompliant: boolean,
  shouldUpdate: boolean,
  useRDAService: boolean,
  accountId: string
}) =>
  !shouldUpdate ||
  (useRDAService && isLogged && accountId && isAccountCompliant);

export const skipTopRacesRDAQuery = ({
  isLogged,
  isAccountCompliant,
  shouldUpdate,
  useRDAService,
  accountId
}: {
  isLogged: boolean,
  isAccountCompliant: boolean,
  shouldUpdate: boolean,
  useRDAService: boolean,
  accountId: boolean
}) =>
  !shouldUpdate ||
  (useRDAService ? !isLogged || !accountId || !isAccountCompliant : true);

export default connect(mapStateToProps)(
  compose(
    graphql(TopRacesQuery, {
      ...ApolloOptions,
      skip: skipTopRacesQuery
    }),
    graphql(TopRacesRDAQuery, {
      ...ApolloOptions,
      skip: skipTopRacesRDAQuery
    }),
    connect((store) => ({
      optedInPromos: get(store, "userData.optedInPromos", {})
    }))
  )(withRouter(FDRTopRaces))
);
